'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Building2, Plus, Edit3, Calendar, User, ShoppingBag, ArrowLeft, ArrowRight, Trash2, X, Shield, Clock, CheckCircle, XCircle, Package } from 'lucide-react';
import { getAllListings, getAllListingsAdmin, getPendingListings, approveListingById, rejectListingById, deleteListing, reorderListings, getProductsByBusiness, getPendingProducts, approveProduct, rejectProduct, getPendingBusinessEdits, getPendingProductEdits, approveBusinessEdit, rejectBusinessEdit, approveProductEdit, rejectProductEdit, type BusinessListing, type Product, type BusinessEdit, type ProductEdit } from '@/lib/storage';
import Badge from '@/components/ui/Badge';

const ADMIN_CODE = 'qy568';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
      <div className="mb-8">
        <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-30">
          <defs>
            <linearGradient id="emptyGrad" x1="0" y1="72" x2="72" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#1d4ed8" />
              <stop offset="100%" stopColor="#60a5fa" />
            </linearGradient>
          </defs>
          <line x1="4" y1="56" x2="68" y2="56" stroke="url(#emptyGrad)" strokeWidth="3" strokeLinecap="round" />
          <line x1="4" y1="56" x2="36" y2="16" stroke="url(#emptyGrad)" strokeWidth="3" strokeLinecap="round" />
          <line x1="36" y1="16" x2="68" y2="56" stroke="url(#emptyGrad)" strokeWidth="3" strokeLinecap="round" />
          <line x1="12" y1="44" x2="56" y2="24" stroke="url(#emptyGrad)" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
          <line x1="20" y1="20" x2="60" y2="52" stroke="url(#emptyGrad)" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
        </svg>
      </div>
      <h2 className="text-2xl font-semibold text-zinc-200 mb-3">No businesses listed yet</h2>
      <p className="text-zinc-500 text-base max-w-sm mb-8 leading-relaxed">
        GigaMarketplace is open to everyone. Be the first to add your business — no account required.
      </p>
      <Link
        href="/add-business"
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white font-semibold rounded-xl transition-all duration-150 shadow-lg shadow-blue-900/30"
      >
        <Plus size={18} />
        Add the First Business
      </Link>
    </div>
  );
}

function BusinessCard({ listing }: { listing: BusinessListing }) {
  const accentColor = listing.color || '#2563eb';
  return (
    <div
      className="group relative bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-2xl p-6 transition-all duration-200 hover:shadow-xl hover:shadow-black/40 hover:-translate-y-0.5"
      style={{ borderTopColor: accentColor, borderTopWidth: '3px' }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {listing.logoUrl && (
            <img
              src={listing.logoUrl}
              alt={`${listing.businessName} logo`}
              className="w-10 h-10 object-contain rounded-lg border border-zinc-700 bg-zinc-800 flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-zinc-100 truncate leading-tight mb-1">
              {listing.businessName}
            </h3>
            <div className="flex items-center gap-1.5 text-sm text-zinc-500">
              <User size={13} className="flex-shrink-0" />
              <span className="truncate">{listing.ownerName}</span>
            </div>
          </div>
        </div>
        <Badge category={listing.category} />
      </div>

      <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3 mb-4">
        {listing.description}
      </p>

      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
        <div className="flex items-center gap-1.5 text-xs text-zinc-600">
          <Calendar size={12} />
          <span>{formatDate(listing.createdAt)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href={`/edit-business?id=${listing.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 border border-zinc-700 hover:border-blue-500/40 rounded-lg transition-all duration-150 opacity-0 group-hover:opacity-100"
            aria-label={`Edit ${listing.businessName}`}
          >
            <Edit3 size={12} />
            Edit listing
          </Link>
          <Link
            href={`/business-products?id=${listing.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white active:scale-95 rounded-lg transition-all duration-150 shadow-md"
            style={{ backgroundColor: accentColor }}
            aria-label={`Browse products from ${listing.businessName}`}
          >
            <ShoppingBag size={12} />
            Browse
          </Link>
        </div>
      </div>
    </div>
  );
}

function MyPendingBusinessCard({ listing }: { listing: BusinessListing }) {
  const accentColor = listing.color || '#2563eb';
  return (
    <div
      className="group relative bg-zinc-900 border border-zinc-700 rounded-2xl p-6 transition-all duration-200"
      style={{ borderTopColor: accentColor, borderTopWidth: '3px' }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {listing.logoUrl && (
            <img
              src={listing.logoUrl}
              alt={`${listing.businessName} logo`}
              className="w-10 h-10 object-contain rounded-lg border border-zinc-700 bg-zinc-800 flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-zinc-100 truncate leading-tight mb-1">
              {listing.businessName}
            </h3>
            <div className="flex items-center gap-1.5 text-sm text-zinc-500">
              <User size={13} className="flex-shrink-0" />
              <span className="truncate">{listing.ownerName}</span>
            </div>
          </div>
        </div>
        <Badge category={listing.category} />
      </div>

      <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3 mb-4">
        {listing.description}
      </p>

      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
        <div className="flex items-center gap-1.5 text-xs text-zinc-600">
          <Calendar size={12} />
          <span>{formatDate(listing.createdAt)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href={`/edit-business?id=${listing.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 border border-zinc-700 hover:border-blue-500/40 rounded-lg transition-all duration-150 opacity-0 group-hover:opacity-100"
            aria-label={`Edit ${listing.businessName}`}
          >
            <Edit3 size={12} />
            Edit listing
          </Link>
          <Link
            href={`/business-products?id=${listing.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white active:scale-95 rounded-lg transition-all duration-150 shadow-md"
            style={{ backgroundColor: accentColor }}
            aria-label={`Browse products from ${listing.businessName}`}
          >
            <ShoppingBag size={12} />
            Browse
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Approval Panel ────────────────────────────────────────────────────────────

interface PendingBusinessCardProps {
  listing: BusinessListing;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

function PendingBusinessCard({ listing, onApprove, onReject }: PendingBusinessCardProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    getProductsByBusiness(listing.id)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [listing.id]);

  const handleApprove = async () => {
    setActioning(true);
    await onApprove(listing.id);
  };

  const handleReject = async () => {
    setActioning(true);
    await onReject(listing.id);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-800">
        <div className="flex items-start gap-3">
          {listing.logoUrl ? (
            <img
              src={listing.logoUrl}
              alt={`${listing.businessName} logo`}
              className="w-12 h-12 object-contain rounded-xl border border-zinc-700 bg-zinc-800 flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl border border-zinc-700 bg-zinc-800 flex-shrink-0 flex items-center justify-center">
              <Building2 size={20} className="text-zinc-600" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-zinc-100">{listing.businessName}</h3>
              <span
                className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: listing.color || '#2563eb' }}
                title={`Brand color: ${listing.color}`}
              />
            </div>
            <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-0.5">
              <User size={11} />
              <span>{listing.ownerName}</span>
              <span className="text-zinc-700">·</span>
              <Badge category={listing.category} />
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-2 py-1 flex-shrink-0">
            <Clock size={11} />
            <span>Pending</span>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="px-5 py-3 border-b border-zinc-800">
        <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-1.5">Description</p>
        <p className="text-sm text-zinc-300 leading-relaxed">{listing.description}</p>
      </div>

      {/* Products */}
      <div className="px-5 py-3 border-b border-zinc-800">
        <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">
          Products {!loadingProducts && `(${products.length})`}
        </p>
        {loadingProducts ? (
          <p className="text-xs text-zinc-600">Loading products...</p>
        ) : products.length === 0 ? (
          <p className="text-xs text-zinc-600 italic">No products added</p>
        ) : (
          <div className="space-y-2">
            {products.map((product) => (
              <div key={product.id} className="flex items-center gap-3 bg-zinc-800/60 rounded-xl px-3 py-2">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-10 h-10 object-cover rounded-lg border border-zinc-700 flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg border border-zinc-700 bg-zinc-800 flex-shrink-0 flex items-center justify-center">
                    <Package size={14} className="text-zinc-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-200 truncate">{product.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{product.description}</p>
                </div>
                <span className="text-sm font-semibold text-green-400 flex-shrink-0">${product.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-3 flex items-center gap-3">
        <button
          onClick={handleApprove}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150"
        >
          <CheckCircle size={14} />
          Approve
        </button>
        <button
          onClick={handleReject}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-red-600/20 hover:bg-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-red-400 font-semibold text-sm border border-red-500/30 rounded-xl transition-all duration-150"
        >
          <XCircle size={14} />
          Reject
        </button>
      </div>
    </div>
  );
}

// ── Pending Product Card ──────────────────────────────────────────────────────

interface PendingProductCardProps {
  product: Product;
  businessName: string;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

function PendingProductCard({ product, businessName, onApprove, onReject }: PendingProductCardProps) {
  const [actioning, setActioning] = useState(false);

  const handleApprove = async () => {
    setActioning(true);
    await onApprove(product.id);
  };

  const handleReject = async () => {
    setActioning(true);
    await onReject(product.id);
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden">
      <div className="flex gap-4 p-4">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-20 h-20 object-cover rounded-xl border border-zinc-700 flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 rounded-xl border border-zinc-700 bg-zinc-800 flex-shrink-0 flex items-center justify-center">
            <Package size={24} className="text-zinc-600" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-sm font-semibold text-zinc-100 leading-snug">{product.name}</h3>
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-400 border border-amber-400/20 flex-shrink-0">
              <Clock size={10} />
              Pending
            </span>
          </div>
          <p className="text-xs text-zinc-500 mb-1 truncate">From: <span className="text-zinc-400">{businessName}</span></p>
          <p className="text-xs text-zinc-500 line-clamp-2 mb-2">{product.description}</p>
          <p className="text-sm font-bold text-green-400">${product.price.toFixed(2)}</p>
        </div>
      </div>
      <div className="px-4 pb-4 flex items-center gap-3">
        <button
          onClick={handleApprove}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150"
        >
          <CheckCircle size={14} />
          Approve
        </button>
        <button
          onClick={handleReject}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-red-600/20 hover:bg-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-red-400 font-semibold text-sm border border-red-500/30 rounded-xl transition-all duration-150"
        >
          <XCircle size={14} />
          Reject
        </button>
      </div>
    </div>
  );
}

// ── Pending Edit Card ─────────────────────────────────────────────────────────

interface PendingEditCardProps {
  edit: BusinessEdit;
  originalListing: BusinessListing | null;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

function DiffRow({ label, oldVal, newVal }: { label: string; oldVal: string; newVal: string }) {
  let changed = oldVal !== newVal;
  return (
    <div className="mb-3">
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">{label}</p>
      {changed ? (
        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <span className="text-xs font-bold text-red-400 mt-0.5 flex-shrink-0">−</span>
            <p className="text-xs text-red-300 line-through leading-relaxed break-all">{oldVal || '(empty)'}</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-xs font-bold text-emerald-400 mt-0.5 flex-shrink-0">+</span>
            <p className="text-xs text-emerald-300 leading-relaxed break-all">{newVal || '(empty)'}</p>
          </div>
        </div>
      ) : (
        <p className="text-xs text-zinc-500 italic">No change</p>
      )}
    </div>
  );
}

function PendingEditCard({ edit, originalListing, onApprove, onReject }: PendingEditCardProps) {
  const [actioning, setActioning] = useState(false);

  const handleApprove = async () => {
    setActioning(true);
    await onApprove(edit.id);
  };

  const handleReject = async () => {
    setActioning(true);
    await onReject(edit.id);
  };

  const orig = originalListing;

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 size={16} className="text-blue-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-100 truncate">{orig?.businessName || edit.businessName}</p>
            <p className="text-xs text-zinc-500">Business edit pending review</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-2 py-1 flex-shrink-0">
          <Clock size={11} />
          <span>Pending</span>
        </div>
      </div>

      {/* Diff */}
      <div className="px-5 py-4 border-b border-zinc-800 space-y-1">
        <DiffRow label="Business Name" oldVal={orig?.businessName || ''} newVal={edit.businessName} />
        <DiffRow label="Description" oldVal={orig?.description || ''} newVal={edit.description} />
        <DiffRow label="Category" oldVal={orig?.category || ''} newVal={edit.category} />
        <DiffRow label="Owner Name" oldVal={orig?.ownerName || ''} newVal={edit.ownerName} />
        {/* Color diff */}
        {orig?.color !== edit.color && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Brand Color</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-red-400">−</span>
                <span className="w-5 h-5 rounded-full border border-zinc-600 flex-shrink-0" style={{ backgroundColor: orig?.color || '#2563eb' }} />
                <span className="text-xs text-red-300 line-through">{orig?.color}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-emerald-400">+</span>
                <span className="w-5 h-5 rounded-full border border-zinc-600 flex-shrink-0" style={{ backgroundColor: edit.color }} />
                <span className="text-xs text-emerald-300">{edit.color}</span>
              </div>
            </div>
          </div>
        )}
        {/* Logo diff */}
        {orig?.logoUrl !== edit.logoUrl && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Logo</p>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-xs text-red-400 mb-1">Before</p>
                {orig?.logoUrl ? (
                  <img src={orig.logoUrl} alt="Old logo" className="w-12 h-12 object-contain rounded-lg border border-zinc-700 bg-zinc-800" />
                ) : (
                  <div className="w-12 h-12 rounded-lg border border-zinc-700 bg-zinc-800 flex items-center justify-center">
                    <Building2 size={16} className="text-zinc-600" />
                  </div>
                )}
              </div>
              <span className="text-zinc-600">→</span>
              <div className="text-center">
                <p className="text-xs text-emerald-400 mb-1">After</p>
                {edit.logoUrl ? (
                  <img src={edit.logoUrl} alt="New logo" className="w-12 h-12 object-contain rounded-lg border border-zinc-700 bg-zinc-800" />
                ) : (
                  <div className="w-12 h-12 rounded-lg border border-zinc-700 bg-zinc-800 flex items-center justify-center">
                    <Building2 size={16} className="text-zinc-600" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-3 flex items-center gap-3">
        <button
          onClick={handleApprove}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150"
        >
          <CheckCircle size={14} />
          Approve
        </button>
        <button
          onClick={handleReject}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-red-600/20 hover:bg-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-red-400 font-semibold text-sm border border-red-500/30 rounded-xl transition-all duration-150"
        >
          <XCircle size={14} />
          Reject
        </button>
      </div>
    </div>
  );
}

// ── Pending Product Edit Card ─────────────────────────────────────────────────

interface PendingProductEditCardProps {
  edit: ProductEdit;
  originalProduct: Product | null;
  businessName: string;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

function PendingProductEditCard({ edit, originalProduct, businessName, onApprove, onReject }: PendingProductEditCardProps) {
  const [actioning, setActioning] = useState(false);

  const handleApprove = async () => {
    setActioning(true);
    await onApprove(edit.id);
  };

  const handleReject = async () => {
    setActioning(true);
    await onReject(edit.id);
  };

  const orig = originalProduct;

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Package size={16} className="text-blue-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-100 truncate">{orig?.name || edit.name}</p>
            <p className="text-xs text-zinc-500 truncate">From: {businessName}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-2 py-1 flex-shrink-0">
          <Clock size={11} />
          <span>Pending</span>
        </div>
      </div>

      <div className="px-5 py-4 border-b border-zinc-800">
        <DiffRow label="Name" oldVal={orig?.name || ''} newVal={edit.name} />
        <DiffRow label="Description" oldVal={orig?.description || ''} newVal={edit.description} />
        <DiffRow label="Price" oldVal={orig ? `$${orig.price.toFixed(2)}` : ''} newVal={`$${edit.price.toFixed(2)}`} />
        {orig?.imageUrl !== edit.imageUrl && edit.imageUrl && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Image</p>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-xs text-red-400 mb-1">Before</p>
                {orig?.imageUrl ? (
                  <img src={orig.imageUrl} alt="Old" className="w-16 h-16 object-cover rounded-lg border border-zinc-700" />
                ) : (
                  <div className="w-16 h-16 rounded-lg border border-zinc-700 bg-zinc-800 flex items-center justify-center">
                    <Package size={16} className="text-zinc-600" />
                  </div>
                )}
              </div>
              <span className="text-zinc-600">→</span>
              <div className="text-center">
                <p className="text-xs text-emerald-400 mb-1">After</p>
                <img src={edit.imageUrl} alt="New" className="w-16 h-16 object-cover rounded-lg border border-zinc-700" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-3 flex items-center gap-3">
        <button
          onClick={handleApprove}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150"
        >
          <CheckCircle size={14} />
          Approve
        </button>
        <button
          onClick={handleReject}
          disabled={actioning}
          className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-red-600/20 hover:bg-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 text-red-400 font-semibold text-sm border border-red-500/30 rounded-xl transition-all duration-150"
        >
          <XCircle size={14} />
          Reject
        </button>
      </div>
    </div>
  );
}

// ── Admin Panel ───────────────────────────────────────────────────────────────

type AdminTab = 'pending' | 'pendingProducts' | 'edits' | 'all';

function AdminPanel({ onClose, onReorder }: { onClose: () => void; onReorder?: () => void }) {
  const [tab, setTab] = useState<AdminTab>('pending');
  const [pendingListings, setPendingListings] = useState<BusinessListing[]>([]);
  const [allListings, setAllListings] = useState<BusinessListing[]>([]);
  const [pendingProducts, setPendingProducts] = useState<Product[]>([]);
  const [pendingEdits, setPendingEdits] = useState<BusinessEdit[]>([]);
  const [pendingProductEdits, setPendingProductEdits] = useState<ProductEdit[]>([]);
  const [businessMap, setBusinessMap] = useState<Record<string, string>>({});
  const [allListingsMap, setAllListingsMap] = useState<Record<string, BusinessListing>>({});
  const [allProductsMap, setAllProductsMap] = useState<Record<string, Product>>({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, all, products, bEdits, pEdits] = await Promise.all([
        getPendingListings(),
        getAllListingsAdmin(),
        getPendingProducts(),
        getPendingBusinessEdits(),
        getPendingProductEdits(),
      ]);
      setPendingListings(pending);
      setAllListings(all);
      setPendingProducts(products);
      setPendingEdits(bEdits);
      setPendingProductEdits(pEdits);

      const map: Record<string, string> = {};
      const listingsMap: Record<string, BusinessListing> = {};
      all.forEach((l) => {
        map[l.id] = l.businessName;
        listingsMap[l.id] = l;
      });
      setBusinessMap(map);
      setAllListingsMap(listingsMap);

      // Build product map from all products across all businesses
      const prodMap: Record<string, Product> = {};
      products.forEach((p) => { prodMap[p.id] = p; });
      setAllProductsMap(prodMap);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleApprove = async (id: string) => {
    try {
      await approveListingById(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectListingById(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleApproveProduct = async (id: string) => {
    try {
      await approveProduct(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleRejectProduct = async (id: string) => {
    try {
      await rejectProduct(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleApproveEdit = async (id: string) => {
    try {
      await approveBusinessEdit(id);
      await refresh();
      onReorder?.();
    } catch {
      // ignore
    }
  };

  const handleRejectEdit = async (id: string) => {
    try {
      await rejectBusinessEdit(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleApproveProductEdit = async (id: string) => {
    try {
      await approveProductEdit(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleRejectProductEdit = async (id: string) => {
    try {
      await rejectProductEdit(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleRemove = async (id: string, name: string) => {
    if (!window.confirm(`Remove "${name}" from the directory?`)) return;
    try {
      await deleteListing(id);
      await refresh();
    } catch {
      // ignore
    }
  };

  const handleMove = async (index: number, direction: 'left' | 'right') => {
    const approvedListings = allListings
      .filter((l) => l.status === 'approved')
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const newList = [...approvedListings];
    const swapIdx = direction === 'left' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    try {
      await reorderListings(newList.map((l) => l.id));
      await refresh();
      onReorder?.();
    } catch {
      await refresh();
    }
  };

  const approvedListings = allListings
    .filter((l) => l.status === 'approved')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const totalEditsBadge = pendingEdits.length + pendingProductEdits.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-zinc-950 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-blue-400" />
            <h2 className="text-sm font-semibold text-zinc-100">Admin Panel</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 rounded-lg transition-colors"
            aria-label="Close admin panel"
          >
            <X size={15} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800 px-5 overflow-x-auto">
          <button
            onClick={() => setTab('pending')}
            className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors mr-5 whitespace-nowrap ${
              tab === 'pending' ? 'border-amber-400 text-amber-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Clock size={14} />
            Businesses
            {pendingListings.length > 0 && (
              <span className="bg-amber-400/20 text-amber-400 text-xs font-bold px-1.5 py-0.5 rounded-full">
                {pendingListings.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('pendingProducts')}
            className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors mr-5 whitespace-nowrap ${
              tab === 'pendingProducts' ? 'border-amber-400 text-amber-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Package size={14} />
            Products
            {pendingProducts.length > 0 && (
              <span className="bg-amber-400/20 text-amber-400 text-xs font-bold px-1.5 py-0.5 rounded-full">
                {pendingProducts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('edits')}
            className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors mr-5 whitespace-nowrap ${
              tab === 'edits' ? 'border-purple-400 text-purple-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Edit3 size={14} />
            Edits
            {totalEditsBadge > 0 && (
              <span className="bg-purple-400/20 text-purple-400 text-xs font-bold px-1.5 py-0.5 rounded-full">
                {totalEditsBadge}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('all')}
            className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === 'all' ? 'border-blue-400 text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Building2 size={14} />
            Approved ({approvedListings.length})
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4">
          {loading ? (
            <p className="text-center text-zinc-600 text-sm py-8">Loading...</p>
          ) : tab === 'pending' ? (
            pendingListings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle size={32} className="text-green-500/40 mb-3" />
                <p className="text-zinc-500 text-sm">No pending businesses — all caught up!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingListings.map((listing) => (
                  <PendingBusinessCard
                    key={listing.id}
                    listing={listing}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                ))}
              </div>
            )
          ) : tab === 'pendingProducts' ? (
            pendingProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle size={32} className="text-green-500/40 mb-3" />
                <p className="text-zinc-500 text-sm">No pending products — all caught up!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingProducts.map((product) => (
                  <PendingProductCard
                    key={product.id}
                    product={product}
                    businessName={businessMap[product.businessId] || product.businessId}
                    onApprove={handleApproveProduct}
                    onReject={handleRejectProduct}
                  />
                ))}
              </div>
            )
          ) : tab === 'edits' ? (
            pendingEdits.length === 0 && pendingProductEdits.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle size={32} className="text-green-500/40 mb-3" />
                <p className="text-zinc-500 text-sm">No pending edits — all caught up!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingEdits.length > 0 && (
                  <>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide px-1">Business Edits ({pendingEdits.length})</p>
                    {pendingEdits.map((edit) => (
                      <PendingEditCard
                        key={edit.id}
                        edit={edit}
                        originalListing={allListingsMap[edit.businessId] || null}
                        onApprove={handleApproveEdit}
                        onReject={handleRejectEdit}
                      />
                    ))}
                  </>
                )}
                {pendingProductEdits.length > 0 && (
                  <>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide px-1 mt-2">Product Edits ({pendingProductEdits.length})</p>
                    {pendingProductEdits.map((edit) => (
                      <PendingProductEditCard
                        key={edit.id}
                        edit={edit}
                        originalProduct={allProductsMap[edit.productId] || null}
                        businessName={businessMap[edit.businessId] || edit.businessId}
                        onApprove={handleApproveProductEdit}
                        onReject={handleRejectProductEdit}
                      />
                    ))}
                  </>
                )}
              </div>
            )
          ) : (
            approvedListings.length === 0 ? (
              <p className="text-center text-zinc-600 text-sm py-8">No approved businesses yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {approvedListings.map((listing, index) => (
                  <div
                    key={listing.id}
                    className="flex flex-col bg-zinc-900 border border-zinc-800 rounded-xl p-3 gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {listing.logoUrl ? (
                        <img src={listing.logoUrl} alt="" className="w-7 h-7 object-contain rounded-lg border border-zinc-700 bg-zinc-800 flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-lg border border-zinc-700 bg-zinc-800 flex-shrink-0 flex items-center justify-center">
                          <Building2 size={12} className="text-zinc-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-zinc-200 truncate leading-tight">{listing.businessName}</p>
                        <p className="text-xs text-zinc-600 font-mono">#{index + 1}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-1">
                      <button
                        onClick={() => handleMove(index, 'left')}
                        disabled={index === 0}
                        className="flex-1 flex items-center justify-center p-1.5 text-zinc-500 hover:text-zinc-200 disabled:opacity-20 disabled:cursor-not-allowed hover:bg-zinc-700 rounded-lg transition-colors"
                        aria-label="Move left"
                      >
                        <ArrowLeft size={12} />
                      </button>
                      <button
                        onClick={() => handleMove(index, 'right')}
                        disabled={index === approvedListings.length - 1}
                        className="flex-1 flex items-center justify-center p-1.5 text-zinc-500 hover:text-zinc-200 disabled:opacity-20 disabled:cursor-not-allowed hover:bg-zinc-700 rounded-lg transition-colors"
                        aria-label="Move right"
                      >
                        <ArrowRight size={12} />
                      </button>
                      <button
                        onClick={() => handleRemove(listing.id, listing.businessName)}
                        className="flex-1 flex items-center justify-center p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        aria-label="Remove business"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ── Secret Dot + Access Code Modal ────────────────────────────────────────────

function SecretDot({ onReorder }: { onReorder?: () => void }) {
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleDotClick = () => {
    setCode('');
    setError(false);
    setShowCodeModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code === ADMIN_CODE) {
      setShowCodeModal(false);
      setShowAdmin(true);
      setCode('');
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <>
      <button
        onClick={handleDotClick}
        className="w-2 h-2 rounded-full bg-zinc-700 hover:bg-zinc-600 transition-colors duration-150 flex-shrink-0 opacity-20 hover:opacity-30 -mt-2"
        aria-label="Admin access"
      />

      {showCodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCodeModal(false)} />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-xs shadow-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={16} className="text-blue-400" />
              <h3 className="text-sm font-semibold text-zinc-100">Admin Access</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <input
                type="password"
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(false); }}
                placeholder="Enter access code..."
                autoFocus
                className={`w-full bg-zinc-800 border ${error ? 'border-red-500' : 'border-zinc-700'} rounded-xl px-4 py-2.5 text-sm font-mono text-zinc-100 placeholder-zinc-600 placeholder:font-sans focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150 mb-1`}
              />
              {error && <p className="text-xs text-red-400 mb-3">Incorrect code.</p>}
              <button
                type="submit"
                className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150"
              >
                Enter
              </button>
            </form>
          </div>
        </div>
      )}

      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} onReorder={onReorder} />}
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function BusinessListingsClient() {
  const [listings, setListings] = useState<BusinessListing[]>([]);
  const [myPendingListings, setMyPendingListings] = useState<BusinessListing[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMyPendingListings = useCallback(() => {
    try {
      const myBusinesses: Record<string, BusinessListing> = JSON.parse(localStorage.getItem('giga_my_businesses') || '{}');
      const pending = Object.values(myBusinesses).filter((b) => b.status === 'pending');
      setMyPendingListings(pending);
    } catch {
      setMyPendingListings([]);
    }
  }, []);

  const fetchListings = useCallback(() => {
    getAllListings()
      .then((approved) => {
        setListings(approved);
        // Remove any of my businesses that are now approved (no longer need local preview)
        try {
          const myBusinesses: Record<string, BusinessListing> = JSON.parse(localStorage.getItem('giga_my_businesses') || '{}');
          const approvedIds = new Set(approved.map((l) => l.id));
          let changed = false;
          Object.keys(myBusinesses).forEach((id) => {
            if (approvedIds.has(id)) {
              delete myBusinesses[id];
              changed = true;
            }
          });
          if (changed) {
            localStorage.setItem('giga_my_businesses', JSON.stringify(myBusinesses));
          }

          // Clean up pending edits for businesses that are now approved (edit was approved)
          // We keep the pending edit until admin re-approves after an edit
          // Actually: if the business is approved AND has a pending edit, the edit hasn't been approved yet
          // We only remove the pending edit when the business is approved AND its updatedAt matches
          // Simple approach: keep pending edits until user visits again after approval
        } catch {
          // ignore
        }
        loadMyPendingListings();
      })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [loadMyPendingListings]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const totalVisible = listings.length + myPendingListings.length;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-10">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-10">
        <div className="flex items-end gap-3">
          <div>
            <h1 className="text-3xl font-bold text-zinc-100 tracking-tight">Business Directory</h1>
            <p className="text-zinc-500 text-sm mt-1">
              {loading ? 'Loading...' : `${listings.length} ${listings.length === 1 ? 'business' : 'businesses'} listed`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <SecretDot onReorder={fetchListings} />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-zinc-900 border border-zinc-800 rounded-2xl p-6 h-52" />
          ))}
        </div>
      ) : totalVisible === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {myPendingListings.map((listing) => (
            <MyPendingBusinessCard key={listing.id} listing={listing} />
          ))}
          {listings.map((listing) => {
            // Check if owner has a pending edit for this approved listing
            let displayListing = listing;
            try {
              const pendingEdits: Record<string, { businessName: string; description: string; category: string; ownerName: string; color: string; logoUrl: string; updatedAt: string }> = JSON.parse(localStorage.getItem('giga_pending_edits') || '{}');
              if (pendingEdits[listing.id]) {
                const edit = pendingEdits[listing.id];
                displayListing = {
                  ...listing,
                  businessName: edit.businessName,
                  description: edit.description,
                  category: edit.category,
                  ownerName: edit.ownerName,
                  color: edit.color,
                  logoUrl: edit.logoUrl || listing.logoUrl,
                };
              }
            } catch {
              // ignore
            }
            return <BusinessCard key={listing.id} listing={displayListing} />;
          })}
        </div>
      )}
    </div>
  );
}